import {
    gd2xyz,
    xyz2gd,
    degree2dms,
    dms2degree,
    xyz2topo,
  } from "./deviation";
  
  // Initial base state
  export const baseState = {
    input_type: "xyz",
    disabledInput: true,
    disabledToolboxButton: false,
    disabledStartStopButton: true,
    enablePlot: false,
    xyz: [],
    referenceX: 0,
    referenceY: 0,
    referenceZ: 0,
    referenceLatDegree: 0,
    referenceLngDegree: 0,
    referenceHeight: 0,
    referenceLatDMS: [0, 0, 0],
    referenceLngDMS: [0, 0, 0],
    east: 0.0,
    north: 0.0,
    up: 0.0,
    data: [],
    xDomain: 100,
    yDomain: 100,
    xZoomDomain: [-7, 7],
    yZoomDomain: [-7, 7],
    scale: 1429,
    range: 100,
    current: [0, 0],
  };
  
  let lastReceivedLat;
  let lastReceivedLng;
  
  // Core functions for manipulating the state
  export function handleNMEA(state, payload) {
    if (lastReceivedLat !== payload.lat || lastReceivedLng !== payload.lon) {
      if (state.enablePlot) {
        const result = getError(
          payload.lat,
          payload.lon,
          parseFloat((payload.alt + payload.geoidal).toFixed(3)),
          state.xyz[0],
          state.xyz[1],
          state.xyz[2]
        );
  
        if (!isNaN(result[0])) {
          state.north = result[0];
          state.east = result[1];
          state.up = result[2];
  
          if (state.data.length > 600) state.data.shift();
  
          let color, symbol;
          switch (payload.quality) {
            case "standalone":
              color = "red";
              symbol = "circle";
              break;
            case "dgps":
              color = "#FABA3E";
              symbol = "circle";
              break;
            case "pps-fix":
              color = "#50BCDF";
              symbol = "circle";
              break;
            case "fix":
              color = "#05F501";
              symbol = "plus";
              break;
            case "float":
              color = "#05F501";
              symbol = "circle";
              break;
            default:
              color = "black";
              symbol = "circle";
          }
  
          if (state.data.length > 1) {
            let last = state.data.pop();
            if (last && (last.x !== result[0] || last.y !== result[1])) {
              state.data.push(last);
            }
          }
  
          state.data.push({ x: result[1], y: result[0], fill: color, symbol });
  
          lastReceivedLat = payload.lat;
          lastReceivedLng = payload.lon;
        }
      }
    }
    return state;
  }
  
  export function enablePlot(state, value) {
    state.enablePlot = value;
    return state;
  }
  
  export function setInputType(state, inputType) {
    state.input_type = inputType;
    return state;
  }
  
  export function setTrueData(state, trueData) {
    state.true1 = trueData.true1;
    state.true2 = trueData.true2;
    state.true3 = trueData.true3;
    return state;
  }
  
  export function setInputXYZ(state, values) {
    state.referenceX = values.referenceX;
    state.referenceY = values.referenceY;
    state.referenceZ = values.referenceZ;
  
    if (
      values.referenceX !== "" &&
      values.referenceX !== "-" &&
      values.referenceY !== "" &&
      values.referenceY !== "-" &&
      values.referenceZ !== "" &&
      values.referenceZ !== "-"
    ) {
      const llh = xyz2gd(
        parseFloat(values.referenceX),
        parseFloat(values.referenceY),
        parseFloat(values.referenceZ)
      );
      state.referenceLatDegree = llh[0].toFixed(8);
      state.referenceLngDegree = llh[1].toFixed(8);
      state.referenceHeight = llh[2].toFixed(3);
  
      const dms = degree2dms(llh[0], llh[1]);
      state.referenceLatDMS = dms.lat;
      state.referenceLngDMS = dms.lng;
    }
    return state;
  }
  
  export function setInputLLHDegree(state, values) {
    state.referenceLatDegree = values.referenceLatDegree;
    state.referenceLngDegree = values.referenceLngDegree;
    state.referenceHeight = values.referenceHeight;
  
    const dms = degree2dms(
      parseFloat(values.referenceLatDegree),
      parseFloat(values.referenceLngDegree)
    );
    state.referenceLatDMS = dms.lat;
    state.referenceLngDMS = dms.lng;
  
    const xyz = gd2xyz(
      parseFloat(values.referenceLatDegree),
      parseFloat(values.referenceLngDegree),
      parseFloat(values.referenceHeight)
    );
    state.referenceX = xyz[0];
    state.referenceY = xyz[1];
    state.referenceZ = xyz[2];
  
    return state;
  }
  
  export function setInputLLHDMS(state, values) {
    state.referenceLatDMS = values.referenceLatDMS;
    state.referenceLngDMS = values.referenceLngDMS;
    state.referenceHeight = parseFloat(values.referenceHeight).toFixed(3);
  
    const llh = dms2degree(values.referenceLatDMS, values.referenceLngDMS);
    state.referenceLatDegree = llh.lat.toFixed(8);
    state.referenceLngDegree = llh.lng.toFixed(8);
  
    const xyz = gd2xyz(
      parseFloat(llh.lat),
      parseFloat(llh.lng),
      parseFloat(values.referenceHeight)
    );
    state.referenceX = xyz[0];
    state.referenceY = xyz[1];
    state.referenceZ = xyz[2];
  
    return state;
  }
  
  export function setDeviationResult(state, result) {
    state.east = result.east;
    state.north = result.north;
    state.up = result.up;
    return state;
  }
  
  export function getError(lat, lon, h, tx, ty, tz) {
    // deg -> xyz
    const xyz = gd2xyz(lat, lon, h);
  
    // xyz - truePos(xyz)
    const xe = xyz[0] - tx;
    const ye = xyz[1] - ty;
    const ze = xyz[2] - tz;
  
    // truePos(xyz) -> truePos(LLH)
    const truePos = xyz2gd(tx, ty, tz);
  
    // 오차 값을 반환
    return xyz2topo(xe, ye, ze, truePos[0], truePos[1]);
  }
  
  
  